import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getContactSubmissions,
  ContactSubmission,
  ContactSubmissionStatus,
  updateContactSubmissionStatus
} from 'api/contactUs';

type StatusFilter = 'all' | 'new' | 'reviewed';
type StatusOption = ContactSubmissionStatus;

const statusOptions: StatusOption[] = ['new', 'reviewed', 'resolved', 'archived'];

const CARD_BG = 'linear-gradient(145deg, rgba(7,59,76,0.05), rgba(10,127,116,0.05))';

const getUserDetails = () => {
  try {
    const rawUser = localStorage.getItem('user-details');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const hasPlatformAdminAccess = (): boolean => {
  const userDetails = getUserDetails();

  if (!userDetails) {
    return false;
  }

  if (userDetails?.isAdmin) {
    return true;
  }

  const roles = Array.isArray(userDetails?.role) ? userDetails.role : [];
  const roleLabels = roles
    .map((role: any) => {
      if (typeof role === 'string') return role;
      return role?.label || role?.name || role?.role_id || '';
    })
    .filter(Boolean)
    .map((label: string) => label.toLowerCase());

  return roleLabels.some((label: string) => label.includes('admin'));
};

const initialsFor = (firstName: string, lastName: string): string => {
  const first = firstName?.trim()?.charAt(0) || '';
  const last = lastName?.trim()?.charAt(0) || '';
  return `${first}${last}`.toUpperCase() || '?';
};

const formatDateTime = (dateValue: string): string => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const statusColorMap: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
  new: 'warning',
  reviewed: 'success',
  inprogress: 'info'
};

const ContactSubmissionsTab: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState('');
  const [statusDrafts, setStatusDrafts] = useState<Record<string, StatusOption>>({});
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewMessage, setViewMessage] = useState<ContactSubmission | null>(null);

  const isPlatformAdmin = useMemo(() => hasPlatformAdminAccess(), []);

  const loadSubmissions = async (nextFilter: StatusFilter = statusFilter) => {
    if (!isPlatformAdmin) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await getContactSubmissions(nextFilter);
      const nextSubmissions = Array.isArray(response.submissions) ? response.submissions : [];
      setSubmissions(nextSubmissions);
      setStatusDrafts((prev) => {
        const nextDrafts = { ...prev };
        nextSubmissions.forEach((submission) => {
          const statusValue = (submission.status || 'new').toLowerCase() as StatusOption;
          nextDrafts[submission.id] = statusOptions.includes(statusValue) ? statusValue : 'new';
        });
        return nextDrafts;
      });
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.error || 'Failed to fetch submissions.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions(statusFilter);
  }, [statusFilter]);

  const handleStatusDraftChange = (id: string, value: StatusOption) => {
    setStatusDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const handleStatusSave = async (submission: ContactSubmission) => {
    const nextStatus = statusDrafts[submission.id] || 'new';

    if (nextStatus === (submission.status || 'new').toLowerCase()) {
      return;
    }

    setError('');
    setUpdatingId(submission.id);

    try {
      await updateContactSubmissionStatus(submission.id, { status: nextStatus });

      setSubmissions((prev) => {
        const updated = prev.map((row) =>
          row.id === submission.id ? { ...row, status: nextStatus } : row
        );

        if (statusFilter !== 'all') {
          return updated.filter((row) => (row.status || 'new').toLowerCase() === statusFilter);
        }

        return updated;
      });
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.error || 'Failed to update submission status.';
      setError(message);
    } finally {
      setUpdatingId('');
    }
  };

  if (!isPlatformAdmin) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Platform Admin access is required to view Contact Us submissions.
      </Alert>
    );
  }

  return (
    <>
    <Dialog
      open={Boolean(viewMessage)}
      onClose={() => setViewMessage(null)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#073b4c">
            Full Message
          </Typography>
          {viewMessage && (
            <Typography variant="body2" color="text.secondary">
              From: {viewMessage.firstName} {viewMessage.lastName} &mdash; {viewMessage.subject}
            </Typography>
          )}
        </Box>
        <IconButton onClick={() => setViewMessage(null)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {viewMessage?.message}
        </Typography>
      </DialogContent>
    </Dialog>
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #d8e4ea',
        background: CARD_BG
      }}
    >
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} color="#073b4c">
              Contact Us Submissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review incoming enquiries sent from the public contact form.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="All"
              clickable
              color={statusFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('all')}
            />
            <Chip
              label="New"
              clickable
              color={statusFilter === 'new' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('new')}
            />
            <Chip
              label="Reviewed"
              clickable
              color={statusFilter === 'reviewed' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('reviewed')}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadSubmissions(statusFilter)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" justifyContent="center" py={6} spacing={1}>
            <CircularProgress />
            <Typography color="text.secondary">Loading submissions...</Typography>
          </Stack>
        ) : (
          <TableContainer
            sx={{
              borderRadius: 2,
              border: '1px solid #d7dde3',
              backgroundColor: '#ffffff'
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box py={3} textAlign="center">
                        <Typography color="text.secondary">
                          No contact submissions found for the selected filter.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  submissions.map((submission) => {
                    const statusValue = (submission.status || 'new').toLowerCase();
                    return (
                      <TableRow key={submission.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ bgcolor: '#0a7f74', width: 32, height: 32 }}>
                              {initialsFor(submission.firstName, submission.lastName)}
                            </Avatar>
                            <Typography fontWeight={600}>
                              {submission.firstName} {submission.lastName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{submission.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {submission.phone}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          <Typography fontWeight={600}>{submission.subject}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              cursor: submission.message ? 'pointer' : 'default'
                            }}
                            onClick={() => submission.message && setViewMessage(submission)}
                          >
                            {submission.message}
                          </Typography>
                          {submission.message && (
                            <Button
                              size="small"
                              sx={{ mt: 0.5, px: 0, minWidth: 0 }}
                              onClick={() => setViewMessage(submission)}
                            >
                              View message
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusValue.toUpperCase()}
                            color={statusColorMap[statusValue] || 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={statusDrafts[submission.id] || (statusValue as StatusOption)}
                                onChange={(event) =>
                                  handleStatusDraftChange(
                                    submission.id,
                                    event.target.value as StatusOption
                                  )
                                }
                              >
                                {statusOptions.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option.toUpperCase()}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleStatusSave(submission)}
                              disabled={updatingId === submission.id}
                            >
                              {updatingId === submission.id ? 'Saving...' : 'Save'}
                            </Button>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDateTime(submission.submittedAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
    </>
  );
};



export default ContactSubmissionsTab;
